/**
 * How far the view turns per pointer count.
 *
 * The first knob, and the one every shooter is asked for first. It writes to
 * [lookSensitivity](../../../core/input/lookSensitivity.ts), which both control maps read per tick,
 * so a drag on the slider changes the very next frame with a game still running behind the screen.
 *
 * The bounds and the default are that module's, not this file's. A knob names a quantity and hands
 * a value to whoever owns it; owning the range as well would put the same three numbers in two
 * places and let the slider offer a value the game clamps away.
 */

import {
  DEFAULT_LOOK_DEGREES_PER_COUNT,
  MAX_LOOK_DEGREES_PER_COUNT,
  MIN_LOOK_DEGREES_PER_COUNT,
  setLookDegreesPerCount,
} from '@/core/input/lookSensitivity';
import { rangeApply } from '@/ui/settings/controls/rangeSlider';
import { SETTING_LOOK_HINT, SETTING_LOOK_LABEL } from '@/text/settingsText';
import type { SettingDefinition } from '@/ui/settings/settingContract';

/** Fine enough that no two steps feel the same, coarse enough to land on a number twice. */
const STEP = 0.002;

export const LOOK_DEGREES_PER_COUNT: SettingDefinition = {
  id: 'lookDegreesPerCount',
  section: 'controls',
  label: SETTING_LOOK_LABEL,
  hint: SETTING_LOOK_HINT,
  control: {
    kind: 'range',
    min: MIN_LOOK_DEGREES_PER_COUNT,
    max: MAX_LOOK_DEGREES_PER_COUNT,
    step: STEP,
    format: (value: number): string => value.toFixed(3),
  },
  defaultValue: DEFAULT_LOOK_DEGREES_PER_COUNT,
  apply: rangeApply(setLookDegreesPerCount),
};
